import React from 'react';
import { Box, Chip } from '@mui/material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy, // Or horizontalListSortingStrategy if preferred
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { AdvancedTableHeader } from 'ui/modules/reports/pages/TemplatesPage/types'; // Adjust path as needed

interface SortableItemProps {
  id: string;
  title: string;
}

function SortableItem({ id, title }: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: transform ? CSS.Transform.toString(transform) : undefined,
    transition,
    opacity: isDragging ? 0.5 : 1,
    margin: '4px', // Add some spacing between chips
    touchAction: 'none', // Recommended for pointer sensor compatibility
    cursor: isDragging ? 'grabbing' : 'grab', // Change cursor based on dragging state
  };

  return (
    <Box ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <Chip label={title} size="small" />
    </Box>
  );
}

interface DraggableColumnListProps {
  columns: AdvancedTableHeader[];
  onReorder: (reorderedColumns: AdvancedTableHeader[]) => void;
}

const DraggableColumnList: React.FC<DraggableColumnListProps> = ({
  columns,
  onReorder,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = columns.findIndex((col) => col.key === active.id);
      const newIndex = columns.findIndex((col) => col.key === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const reordered = arrayMove(columns, oldIndex, newIndex);
        onReorder(reordered);
      }
    }
  };

  if (!columns || columns.length === 0) {
    return (
      <Box sx={{ p: 1, textAlign: 'center', color: 'grey.500' }}>
        No columns to display or reorder.
      </Box>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={columns.map((col) => col.key)}
        strategy={verticalListSortingStrategy}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            p: 1,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          {columns.map((column) => (
            <SortableItem
              key={column.key}
              id={column.key}
              title={column.label}
            />
          ))}
        </Box>
      </SortableContext>
    </DndContext>
  );
};

export default DraggableColumnList;
